import moment from 'moment-jalaali';
import momentTz from 'moment-timezone';

// Extend moment with timezone
moment.tz = momentTz.tz;
moment.defaultZone = momentTz.defaultZone;

// Set default timezone to Tehran
if (moment.tz) {
  moment.tz.setDefault('Asia/Tehran');
}

/**
 * Formats a date to Persian (Jalali) date string
 * @param {string|Date} date - The date to format
 * @param {string} format - The format string (default: 'jYYYY/jMM/jDD')
 * @returns {string} Formatted Persian date string
 */
export const formatPersianDate = (date, format = 'jYYYY/jMM/jDD') => {
  if (!date) return '';
  
  try {
    // Convert to moment with Tehran timezone
    const momentDate = moment(date).tz('Asia/Tehran');
    
    // Convert to Jalali and format (LTR format: yyyy/mm/dd)
    const formatted = momentDate.format(format);
    // Add LTR mark to ensure left-to-right display
    return '\u202D' + formatted + '\u202C';
  } catch (error) {
    console.error('Error formatting date:', error);
    return '';
  }
};

/**
 * Formats a date to Persian (Jalali) date string with time
 * @param {string|Date} date - The date to format
 * @returns {string} Formatted Persian date string with time
 */
export const formatPersianDateTime = (date) => {
  if (!date) return '';
  
  try {
    // Convert to moment with Tehran timezone
    const momentDate = moment(date).tz('Asia/Tehran');
    
    // Convert to Jalali and format with time (using - separator, LTR format: yyyy/mm/dd-hh:mm)
    const formatted = momentDate.format('jYYYY/jMM/jDD-HH:mm');
    // Add LTR mark to ensure left-to-right display
    return '\u202D' + formatted + '\u202C';
  } catch (error) {
    console.error('Error formatting date time:', error);
    return '';
  }
};

/**
 * Formats a date to Persian (Jalali) date string (short format)
 * @param {string|Date} date - The date to format
 * @returns {string} Formatted Persian date string
 */
export const formatPersianDateShort = (date) => {
  return formatPersianDate(date, 'jYYYY/jMM/jDD');
};

/**
 * Gets current Persian date in format jYYYY/jMM/jDD
 * @returns {string} Current Persian date
 */
export const getCurrentPersianDate = () => {
  return moment().tz('Asia/Tehran').format('jYYYY/jMM/jDD');
};

/**
 * Gets current Persian time in format HH:mm
 * @returns {string} Current Persian time
 */
export const getCurrentPersianTime = () => {
  return moment().tz('Asia/Tehran').format('HH:mm');
};

/**
 * Converts Persian (Jalali) date string to ISO datetime string for datetime-local input
 * Supports formats: "jYYYY/jMM/jDD-HH:mm" or "jYYYY/jMM/jDD HH:mm" or "jYYYY/jMM/jDD"
 * @param {string} persianDate - Persian date string
 * @returns {string} ISO datetime string (YYYY-MM-DDTHH:mm)
 */
export const persianDateToISO = (persianDate) => {
  if (!persianDate) return '';
  
  try {
    // Parse Persian date string - support both "-" and " " as separator
    let datePart, timePart;
    if (persianDate.includes('-')) {
      const parts = persianDate.split('-');
      datePart = parts[0]; // jYYYY/jMM/jDD
      timePart = parts[1] || '00:00'; // HH:mm
    } else {
      const parts = persianDate.split(' ');
      datePart = parts[0]; // jYYYY/jMM/jDD
      timePart = parts[1] || '00:00'; // HH:mm
    }
    
    const [year, month, day] = datePart.split('/').map(Number);
    const [hour, minute] = timePart.split(':').map(Number);
    
    // Convert Jalali to Gregorian using moment-jalaali
    const jalaliMoment = moment(`${year}/${month}/${day} ${hour}:${minute}`, 'jYYYY/jMM/jDD HH:mm');
    const gregorianMoment = moment(jalaliMoment.toDate());
    
    // Format as ISO string for datetime-local input
    return gregorianMoment.format('YYYY-MM-DDTHH:mm');
  } catch (error) {
    console.error('Error converting Persian date to ISO:', error);
    return '';
  }
};

/**
 * Converts ISO datetime string to Persian (Jalali) date string
 * @param {string} isoDate - ISO datetime string (YYYY-MM-DDTHH:mm or YYYY-MM-DD)
 * @returns {string} Persian date string in format "jYYYY/jMM/jDD-HH:mm"
 */
export const isoToPersianDate = (isoDate) => {
  if (!isoDate) return '';
  
  try {
    const momentDate = moment(isoDate).tz('Asia/Tehran');
    const formatted = momentDate.format('jYYYY/jMM/jDD-HH:mm');
    // Add LTR mark to ensure left-to-right display
    return '\u202D' + formatted + '\u202C';
  } catch (error) {
    console.error('Error converting ISO to Persian date:', error);
    return '';
  }
};

